// =============================================================================
// Lairo Status — public, customer-facing page.
// =============================================================================
import { Fragment, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useStore } from "../store";
import {
  COMPONENT_STATUS, INCIDENT_STATUS, MAINT_STATUS, IMPACT, OVERALL,
  overall as computeOverall, uptimePct,
} from "../model";
import {
  Icon, StatusDot, Badge, UptimeBars, Logo, ThemeToggle, useTheme,
  fmtTime, fmtDate, fmtDateTime, relTime, toneColor, toneBg, MONTHS, fmtDuration,
} from "../components/shared";

/* ---- top hero banner --------------------------------------------------- */
function Hero({ state }) {
  const cfg = OVERALL[computeOverall(state)];
  return (
    <div className="card fade-up" style={{ padding: "26px 26px", display: "flex", alignItems: "center", gap: 18,
      borderColor: `color-mix(in oklch, ${toneColor(cfg.tone)} 38%, var(--border))`,
      background: `linear-gradient(180deg, ${toneBg(cfg.tone)}, var(--surface))` }}>
      <div style={{ width: 46, height: 46, borderRadius: 12, flexShrink: 0, display: "grid", placeItems: "center",
        background: toneColor(cfg.tone), color: "var(--accent-contrast)" }}>
        <Icon name={cfg.icon} size={24} stroke={2.5} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 22, fontWeight: 700, letterSpacing: "-0.01em" }}>{cfg.title}</div>
        <div className="muted" style={{ fontSize: 13.5, marginTop: 2 }}>Updated {relTime(state.updatedAt)}</div>
      </div>
    </div>
  );
}

/* ---- announcement ------------------------------------------------------ */
function Announcement({ a }) {
  if (!a || !a.active || !a.text) return null;
  const tone = a.tone === "warning" ? "deg" : "mnt";
  return (
    <div className="fade-up" style={{ display: "flex", gap: 12, padding: "14px 16px", borderRadius: "var(--radius)",
      background: toneBg(tone), border: `1px solid color-mix(in oklch, ${toneColor(tone)} 40%, transparent)`,
      color: "var(--text)", marginBottom: 16 }}>
      <span style={{ color: toneColor(tone), marginTop: 1 }}><Icon name={a.tone === "warning" ? "warn" : "bell"} size={17} /></span>
      <div style={{ fontSize: 14, lineHeight: 1.55 }}>{a.text}</div>
    </div>
  );
}

/* ---- update timeline --------------------------------------------------- */
function Timeline({ updates, vocab }) {
  const ordered = [...updates].reverse();
  return (
    <div>
      {ordered.map((u, i) => {
        const tone = vocab[u.status]?.tone || "op";
        return (
          <div key={u.id} style={{ display: "flex", gap: 12, paddingTop: 14 }}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0 }}>
              <StatusDot tone={tone} size={9} />
              {i < ordered.length - 1 && <span style={{ width: 2, flex: 1, background: "var(--border)", marginTop: 4 }} />}
            </div>
            <div style={{ paddingBottom: i < ordered.length - 1 ? 4 : 0, flex: 1 }}>
              <div className="row" style={{ gap: 8, marginBottom: 3 }}>
                <span style={{ fontWeight: 700, fontSize: 13.5, color: toneColor(tone) }}>{vocab[u.status]?.label}</span>
              </div>
              <div style={{ fontSize: 14, lineHeight: 1.55, color: "var(--text)" }}>{u.body}</div>
              <div className="faint mono" style={{ fontSize: 11.5, marginTop: 5 }}>{fmtDateTime(u.at)}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ---- active incident / live maintenance card --------------------------- */
function ActiveIncident({ item }) {
  const isMaint = item.kind === "maintenance";
  const vocab = isMaint ? MAINT_STATUS : INCIDENT_STATUS;
  const tone = vocab[item.status]?.tone || "maj";
  return (
    <div className="card fade-up" style={{ overflow: "hidden", borderColor: `color-mix(in oklch, ${toneColor(tone)} 45%, var(--border))` }}>
      <div style={{ padding: "16px 18px", borderBottom: "1px solid var(--border)", background: toneBg(tone) }}>
        <div className="between">
          <div className="row" style={{ gap: 9 }}>
            <StatusDot tone={tone} pulse size={9} />
            <span style={{ fontSize: 16, fontWeight: 700 }}>{item.title}</span>
          </div>
          <Badge tone={tone} solid>{vocab[item.status]?.label}</Badge>
        </div>
        {isMaint && (
          <div className="muted" style={{ fontSize: 12.5, marginTop: 8, display: "flex", gap: 6, alignItems: "center" }}>
            <Icon name="clock" size={13} /> {fmtDateTime(item.start)} — {fmtTime(item.end)}
          </div>
        )}
      </div>
      <div style={{ padding: "6px 18px 14px" }}>
        <Timeline updates={item.updates} vocab={vocab} />
      </div>
    </div>
  );
}

/* ---- upcoming maintenance (scheduled, not yet live) -------------------- */
function ScheduledCard({ item }) {
  return (
    <div className="card fade-up" style={{ padding: "15px 18px", borderStyle: "dashed",
      borderColor: `color-mix(in oklch, var(--st-mnt) 45%, var(--border))` }}>
      <div className="between" style={{ alignItems: "flex-start" }}>
        <div>
          <div className="row" style={{ gap: 8, marginBottom: 4 }}>
            <span style={{ color: "var(--st-mnt)" }}><Icon name="wrench" size={15} /></span>
            <span style={{ fontWeight: 700, fontSize: 15 }}>{item.title}</span>
          </div>
          <div className="muted" style={{ fontSize: 13, lineHeight: 1.5 }}>{item.updates[0]?.body}</div>
        </div>
        <Badge tone="mnt">Scheduled</Badge>
      </div>
      <div className="mono faint" style={{ fontSize: 12, marginTop: 10, display: "flex", gap: 6, alignItems: "center" }}>
        <Icon name="clock" size={13} /> {fmtDateTime(item.start)} — {fmtTime(item.end)}
      </div>
    </div>
  );
}

/* ---- components list --------------------------------------------------- */
function ComponentRow({ c }) {
  const cfg = COMPONENT_STATUS[c.status];
  const pct = uptimePct(c);
  return (
    <div style={{ padding: "16px 0" }}>
      <div className="between" style={{ marginBottom: 11, alignItems: "flex-start" }}>
        <div>
          <div className="row" style={{ gap: 9 }}>
            <span style={{ fontWeight: 600, fontSize: 15 }}>{c.name}</span>
          </div>
          {c.description && <div className="faint" style={{ fontSize: 12.5, marginTop: 1 }}>{c.description}</div>}
        </div>
        <div className="row" style={{ gap: 8, flexShrink: 0 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: toneColor(cfg.tone) }}>{cfg.label}</span>
          <StatusDot tone={cfg.tone} size={9} pulse={c.status !== "operational"} />
        </div>
      </div>
      <UptimeBars history={c.history} />
      <div className="between" style={{ marginTop: 4, fontSize: 11.5, color: "var(--text-faint)" }}>
        <span></span>
        <span className="mono">{pct.toFixed(2)}% uptime</span>
      </div>
    </div>
  );
}

function GroupBlock({ name, items }) {
  const [open, setOpen] = useState(true);
  const order = ["major", "partial", "degraded", "maintenance", "operational"];
  let worst = "operational";
  items.forEach((c) => { if (order.indexOf(c.status) < order.indexOf(worst)) worst = c.status; });
  const cfg = COMPONENT_STATUS[worst];
  return (
    <div style={{ borderTop: "1px solid var(--border)" }}>
      <button onClick={() => setOpen(!open)} style={{ all: "unset", boxSizing: "border-box", cursor: "pointer",
        display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%",
        padding: "14px 20px", background: "var(--surface-2)" }}>
        <span className="row" style={{ gap: 9 }}>
          <span style={{ transform: open ? "none" : "rotate(-90deg)", transition: "transform .15s", color: "var(--text-faint)", display: "flex" }}>
            <Icon name="chevron" size={16} />
          </span>
          <span style={{ fontWeight: 700, fontSize: 14 }}>{name}</span>
          <span className="faint" style={{ fontSize: 12.5 }}>{items.length} services</span>
        </span>
        <span className="row" style={{ gap: 8 }}>
          <span style={{ fontSize: 12.5, fontWeight: 600, color: toneColor(cfg.tone) }}>{cfg.label}</span>
          <StatusDot tone={cfg.tone} size={8} />
        </span>
      </button>
      {open && (
        <div style={{ padding: "0 20px" }}>
          {items.map((c, i) => (
            <Fragment key={c.id}>
              {i > 0 && <hr className="divider" />}
              <ComponentRow c={c} />
            </Fragment>
          ))}
        </div>
      )}
    </div>
  );
}

function Components({ state }) {
  const top = state.components.filter((c) => !c.group);
  const groups = {};
  state.components.filter((c) => c.group).forEach((c) => { (groups[c.group] ||= []).push(c); });
  return (
    <div className="card fade-up">
      <div style={{ padding: "4px 20px" }}>
        {top.map((c, i) => (
          <Fragment key={c.id}>
            {i > 0 && <hr className="divider" />}
            <ComponentRow c={c} />
          </Fragment>
        ))}
      </div>
      {Object.entries(groups).map(([name, items]) => (
        <GroupBlock key={name} name={name} items={items} />
      ))}
    </div>
  );
}

/* ---- past incident history (grouped by month) ------------------------- */
function HistoryItem({ item }) {
  const isMaint = item.kind === "maintenance";
  const vocab = isMaint ? MAINT_STATUS : INCIDENT_STATUS;
  const impactTone = isMaint ? "mnt" : IMPACT[item.impact]?.tone || "deg";
  const resolvedUpdate = !isMaint ? item.updates.find((u) => u.status === "resolved") : null;
  const endTs = !isMaint ? (item.resolvedAt || resolvedUpdate?.at) : null;
  const durationStr = !isMaint && endTs ? fmtDuration(item.createdAt, endTs) : "";

  return (
    <div>
      <div className="row" style={{ gap: 9, marginBottom: 2, flexWrap: "wrap" }}>
        <span style={{ fontWeight: 700, fontSize: 15 }}>{item.title}</span>
        <Badge tone={impactTone}>{isMaint ? "Maintenance" : IMPACT[item.impact]?.label}</Badge>
        {durationStr && <span className="faint" style={{ fontSize: 12.5 }}>· Resolved in {durationStr}</span>}
      </div>
      <div className="faint mono" style={{ fontSize: 11.5, marginBottom: 10 }}>{fmtDate(item.createdAt, { year: true })}</div>
      <div style={{ paddingLeft: 2 }}><Timeline updates={item.updates} vocab={vocab} /></div>
    </div>
  );
}

function History({ state }) {
  const resolved = [...state.incidents, ...state.maintenances]
    .filter((i) => i.status === "resolved" || i.status === "completed")
    .sort((a, b) => b.createdAt - a.createdAt);

  const byMonth = {};
  resolved.forEach((i) => {
    const d = new Date(i.createdAt);
    const key = `${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
    (byMonth[key] ||= []).push(i);
  });

  return (
    <section style={{ marginTop: 36 }}>
      <h2 style={{ fontSize: 15, fontWeight: 700, margin: "0 0 4px" }}>Past incidents</h2>
      <p className="faint" style={{ fontSize: 13, margin: "0 0 18px" }}>Resolved incidents and completed maintenance.</p>
      {Object.keys(byMonth).length === 0 && (
        <div className="muted" style={{ fontSize: 14, padding: "20px 0" }}>No incidents in the past 90 days.</div>
      )}
      {Object.entries(byMonth).map(([month, items]) => (
        <div key={month} style={{ marginBottom: 26 }}>
          <div className="eyebrow" style={{ paddingBottom: 10, borderBottom: "1px solid var(--border)", marginBottom: 14 }}>{month}</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
            {items.map((i) => <HistoryItem key={i.id} item={i} />)}
          </div>
        </div>
      ))}
    </section>
  );
}

// Subscribe modal removed

/* ---- app shell --------------------------------------------------------- */
export default function PublicPage() {
  const state = useStore();
  const [theme, setTheme] = useTheme();

  if (!state || state.__missing || state.__error) {
    return <LoadingOrError state={state} />;
  }

  const activeIncidents = state.incidents.filter((i) => i.status !== "resolved").sort((a, b) => b.createdAt - a.createdAt);
  const liveMaint = state.maintenances.filter((m) => m.status === "in_progress");
  const upcoming = state.maintenances.filter((m) => m.status === "scheduled").sort((a, b) => a.start - b.start);
  const active = [...activeIncidents, ...liveMaint];

  return (
    <>
      <header style={{ borderBottom: "1px solid var(--border)", background: "color-mix(in oklch, var(--bg) 80%, transparent)",
        backdropFilter: "blur(8px)", position: "sticky", top: 0, zIndex: 40 }}>
        <div className="wrap between" style={{ height: 60 }}>
          <Link to="/" className="row" style={{ gap: 11, textDecoration: "none", color: "var(--text)" }}>
            <Logo height={24} />
            <span style={{ width: 1, height: 20, background: "var(--border-strong)" }} />
            <span className="muted" style={{ fontWeight: 600, fontSize: 15 }}>{state.page.tagline}</span>
          </Link>
          <div className="row" style={{ gap: 12 }}>
            <a className="btn btn-sm btn-ghost" href="/rss.xml" target="_blank" rel="noopener noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, height: 32, padding: "0 10px" }}>
              <Icon name="rss" size={13} /> RSS Feed
            </a>
            <ThemeToggle theme={theme} setTheme={setTheme} />
          </div>
        </div>
      </header>

      <main className="wrap" style={{ padding: "30px 20px 70px" }}>
        <Announcement a={state.announcement} />
        <Hero state={state} />

        {active.length > 0 && (
          <section style={{ marginTop: 18, display: "flex", flexDirection: "column", gap: 14 }}>
            {active.map((i) => <ActiveIncident key={i.id} item={i} />)}
          </section>
        )}

        {upcoming.length > 0 && (
          <section style={{ marginTop: 18 }}>
            <div className="eyebrow" style={{ marginBottom: 10 }}>Scheduled maintenance</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {upcoming.map((m) => <ScheduledCard key={m.id} item={m} />)}
            </div>
          </section>
        )}

        <section style={{ marginTop: 26 }}>
          <Components state={state} />
        </section>

        <History state={state} />
      </main>

      <footer style={{ borderTop: "1px solid var(--border)", padding: "26px 0", marginTop: 20 }}>
        <div className="wrap between" style={{ flexWrap: "wrap", gap: 14 }}>
          <div className="faint" style={{ fontSize: 12.5 }}>
            Powered by Lairo · <a className="link" href="https://app.lairo.ai">app.lairo.ai</a>
          </div>
          <div className="row" style={{ gap: 16, fontSize: 12.5 }}>
            <a className="link faint" style={{ fontWeight: 600, display: "flex", alignItems: "center", gap: 5 }} href="/rss.xml" target="_blank" rel="noopener noreferrer">
              <Icon name="rss" size={12} /> RSS Feed
            </a>
            <Link className="link faint" style={{ fontWeight: 600 }} to="/admin">Admin</Link>
          </div>
        </div>
      </footer>
    </>
  );
}

function LoadingOrError({ state }) {
  const msg = state?.__error
    ? "Couldn't load status data."
    : state?.__missing
    ? "Status page isn't set up yet."
    : "Loading…";
  const detail = state?.__missing
    ? "Run the seed script (npm run seed) to initialize the database."
    : state?.__error || null;
  return (
    <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", padding: 24 }}>
      <div className="card" style={{ padding: "28px 30px", textAlign: "center", maxWidth: 420 }}>
        <div style={{ marginBottom: 8 }}><Logo height={24} /></div>
        <div style={{ fontWeight: 600 }}>{msg}</div>
        {detail && <div className="muted" style={{ fontSize: 13, marginTop: 6 }}>{detail}</div>}
      </div>
    </div>
  );
}
